import { AutoAwesome } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { GRADIENT_PRIMARY } from 'components/AI/AIStyles';
import { useGetApiV5FilesPendingReview } from 'api/v5/files/files';
import { useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { pluralize } from 'utilities/pluralize';
import * as Utilities from 'utilities/utils';
import { DocumentPreviewModal } from 'components/AI/AIFileReview/components/DocumentPreviewModal/DocumentPreviewModal';
import { format, parseISO } from 'date-fns';

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: GRADIENT_PRIMARY,
  borderRadius: '50%',
  color: theme.palette.common.white,
}));

export interface ReviewModalProps {
  onClose: () => void;
  /**
   * Old ugly dependency to refresh the list in parent component
   */
  component: any;
}

export const ReviewModal = ({ onClose, component }: ReviewModalProps) => {
  const [selectedFile, setSelectedFile] = useState<number | null>(null);
  const { data: filesToReview, isLoading } = useGetApiV5FilesPendingReview();

  return (
    <>
      <Modal
        isOpen
        className="customModal customModal--center customModal--alert customModal--w700 customModal--nopadding"
      >
        <ModalHeader className="ModalHeader">
          <button
            type="button"
            onClick={onClose}
            className="close"
            aria-label="Close"
          >
            <i className="icon-close2" />
          </button>
        </ModalHeader>
        <ModalHeader className="modalHeader">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconContainer>
              <AutoAwesome fontSize="small" />
            </IconContainer>
            <div className="text-medium pl-2">
              <div>Files Need Review</div>
            </div>

            {Boolean(filesToReview?.count) && (
              <span className="tag tag--fill" key="test">
                {`${filesToReview!.count} `}
                {pluralize('file', filesToReview!.count)}
              </span>
            )}
          </Stack>
        </ModalHeader>
        <ModalBody className="modalContent" style={{ maxHeight: '50vh' }}>
          <Card elevation={0} sx={{ borderRadius: 0 }}>
            <CardHeader
              title="Incoming Files"
              subheader="Files requiring review and processing"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
              }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {isLoading && <Skeleton width={300} height={50} />}
              {!isLoading && filesToReview?.count === 0 && (
                <Typography>No files to review</Typography>
              )}
              {!isLoading && Boolean(filesToReview?.files.length) && (
                <List>
                  {filesToReview!.files.map((file) => {
                    const logo = Utilities.fileType({
                      content_type: file.content_type,
                    });
                    return (
                      <ListItemButton
                        key={file.id}
                        onClick={() => {
                          if (file.id) {
                            setSelectedFile(file.id);
                          }
                        }}
                      >
                        <ListItemIcon>
                          <span
                            className="fileTtype align-self-center"
                            style={{ color: '#212529' }}
                          >
                            <i className={logo} aria-hidden="true" />
                          </span>
                        </ListItemIcon>
                        <ListItemText
                          primary={file.display_name}
                          secondary={`${file.uploaded_by} • ${format(
                            parseISO(file.created_at),
                            'MM/dd/yyyy',
                          )}`}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </ModalBody>
      </Modal>
      <DocumentPreviewModal
        showModal={!!selectedFile}
        closeModal={() => setSelectedFile(null)}
        id={selectedFile}
        component={component}
      />
    </>
  );
};
